export const DAPP_THEME = {
  bg: "#050505",
  panel: "#0A0A0A",
  neonGreen: "#00ff41",
  neonCyan: "#00e5ff",
  neonPurple: "#d000ff",
  neonAmber: "#ffaa00",
} as const

export const DAPP_LINKS = {
  website: "https://rivyu.xyz",
  telegramChannel: "https://t.me/rivyuofficial",
  telegramDiscussion: "https://t.me/rivyuprotocol",
  telegramBot: "https://t.me/Rivyuofficialbot",
  instagram: "https://www.instagram.com/rivyu_official",
  supportEmail: "support@rivyu.xyz",
} as const

export const TOKENOMICS_DISTRIBUTION = [
  { name: "Mining Rewards", value: 30, tokens: "300M", description: "Pre-TGE distribution via tasks and on-chain claims." },
  { name: "Staking Rewards", value: 20, tokens: "200M", description: "Reserved for live staking pools and long-term yield." },
  { name: "Liquidity Pool", value: 12, tokens: "120M", description: "Assigned to liquidity at TGE with lock commitment." },
  { name: "Founder allocation", value: 12, tokens: "120M", description: "0% unlocked at TGE; 12-month cliff from TGE." },
  { name: "Strategic round", value: 5, tokens: "50M", description: "Reserved for partners and marketing." },
  { name: "Growth & Partnerships", value: 5, tokens: "50M", description: "Growth and partnerships; locked 12 months post-TGE." },
  { name: "Airdrops", value: 8, tokens: "80M", description: "Community incentives for early engagement." },
  { name: "Treasury", value: 5, tokens: "50M", description: "Protocol treasury for governance initiatives." },
  { name: "Public Sale", value: 3, tokens: "30M", description: "Public access tranche at launch." },
] as const

export const STAKING_POOLS = [
  { id: "flex", name: "CORE FLEX", duration: "Flexible", apy: 5, allocation: "15,000,000 RIV" },
  { id: "30d", name: "CRYO STASIS", duration: "30 days", apy: 12, allocation: "25,000,000 RIV" },
  { id: "90d", name: "DEEP FREEZE", duration: "90 days", apy: 25, allocation: "60,000,000 RIV" },
  { id: "365d", name: "ABSOLUTE ZERO", duration: "365 days", apy: 60, allocation: "100,000,000 RIV" },
] as const

export type RoadmapMilestoneStatus = "completed" | "locked" | "future"

export type RoadmapMilestone = {
  phase: string
  dateShort: string
  date: string
  title: string
  detail: string
  status: RoadmapMilestoneStatus
  strata: string
  sig: string
  isTgeFault?: true
}

/**
 * Protocol roadmap — aligned with Rivyu Telegram Mini App copy (en.json `about.roadmap*` keys).
 * Chronological order; `isTgeFault` drives the major-fault visualization on the roadmap page.
 */
export const ROADMAP_MILESTONES: readonly RoadmapMilestone[] = [
  {
    phase: "P01",
    dateShort: "DEC 2025",
    date: "December 25, 2025",
    title: "Website Launch",
    detail: "Official website and documentation.",
    status: "completed" as const,
    strata: "BEDROCK",
    sig: "0xA9F200",
  },
  {
    phase: "P02",
    dateShort: "MAR 2026",
    date: "March 1st Week, 2026",
    title: "DApp Launch",
    detail: "Mining and staking platform goes live. Begin pre-TGE mining phase.",
    status: "completed" as const,
    strata: "CRUST",
    sig: "0xA9F273",
  },
  {
    phase: "P03",
    dateShort: "APR–MAY 2026",
    date: "April 5 – May 7, 2026 (UTC)",
    title: "Easter 2026 Event",
    detail:
      "Went live April 5, 2026 00:00 UTC through April 30, 2026; grace period for opening already-earned eggs until May 7, 2026. Collectible eggs and seasonal loot — the RNG and boost vocabulary that now powers permanent chest tables.",
    status: "completed" as const,
    strata: "CRUST",
    sig: "0xA9F2E6",
  },
  {
    phase: "P04",
    dateShort: "MAY 2026",
    date: "May 1, 2026",
    title: "Loot Chests & Chest Vault",
    detail:
      "Five-tier Chest Vault with provably fair commit–reveal opens, milestone awards, and the Network tab vault — marked complete for go-live.",
    status: "completed" as const,
    strata: "MANTLE",
    sig: "0xA9F359",
  },
  {
    phase: "P05",
    dateShort: "MAY 2026",
    date: "First week of May 2026",
    title: "Idea Lab & Glitch Bounty",
    detail:
      "Launched in the About hub: Idea Lab for pitching and voting on features; Glitch Bounty for responsible security disclosure with RIV rewards after triage.",
    status: "completed" as const,
    strata: "UPPER_MANTLE",
    sig: "0xA9F3CC",
  },
  {
    phase: "P06",
    dateShort: "MAY 2026",
    date: "May 12, 2026",
    title: "Idea Lab — first community approvals",
    detail: "First batch of ideas submitted and voted by the community was approved for implementation.",
    status: "completed" as const,
    strata: "TRANSITION",
    sig: "0xA9F43F",
  },
  {
    phase: "P07",
    dateShort: "MAY 2026",
    date: "May 2026 (live)",
    title: "Weekly Lottery Pool & Data Rift",
    detail:
      "Rewards hub live: on-chain weekly lottery (Bronze / Silver / Gold pools with provable draws, refunds, and dedicated signing keys) plus Data Rift daily coupon board, Rift tokens, fracture cycle, and on-chain RIV transmission buffer.",
    status: "completed" as const,
    strata: "OUTER_CORE",
    sig: "0xA9F4B2",
  },
  {
    phase: "P08",
    dateShort: "JUN 2026",
    date: "June 2026",
    title: "Active Score",
    detail:
      "Percentile engagement scoring goes live — a transparent, mining-weighted reputation earned from mining, staking, ideas, bug reports, ads, and level, recomputed daily. It ranks operators and gates beta access and the governance vote allowance.",
    status: "completed" as const,
    strata: "MAGNETIC_FIELD",
    sig: "0xA9F525",
  },
  {
    phase: "P09",
    dateShort: "JUN 2026",
    date: "June 2026",
    title: "Chest Fusion (Beta)",
    detail:
      "Operators fuse lower-tier chests into higher tiers with provably fair recipes for a RIV fee — a new deflationary sink that concentrates loot and thins circulating supply.",
    status: "completed" as const,
    strata: "TECTONIC_SEAM",
    sig: "0xA9F598",
  },
  {
    phase: "P10",
    dateShort: "JUN 2026",
    date: "June 2026",
    title: "Governance (Voting DAO)",
    detail:
      "Advisory on-chain voting launches: full operators spend an Active-Score vote allowance by burning RIV on multi-option team proposals, recorded on-chain and verified by the chest-shop settlement chain. Outcomes are advisory — the team enacts approved decisions manually.",
    status: "completed" as const,
    strata: "SEISMIC_LAYER",
    sig: "0xA9F60B",
  },
  {
    phase: "P11",
    dateShort: "JUL 2026",
    date: "July 2026",
    title: "Clans",
    detail:
      "Faction-aligned clans go live — non-withdrawable clan treasuries, missions, wars, heists, and a living clan chronicle. Rolling out in beta.",
    status: "completed" as const,
    strata: "CONVERGENCE",
    sig: "0xA9F67E",
  },
  {
    phase: "P12",
    dateShort: "19 SEP 2026",
    date: "19 September 2026 (target)",
    title: "TGE (Token Generation Event)",
    detail: "RIVYU token launches on TON Network. 120M tokens assigned to liquidity pool. Target date subject to completion of the independent audit and legal-entity setup.",
    status: "future" as const,
    strata: "CORE — FAULT_LINE",
    sig: "0xA9F6F1",
    isTgeFault: true,
  },
  {
    phase: "P13",
    dateShort: "SEP 2026",
    date: "September 2026 (post-TGE)",
    title: "First Airdrop",
    detail: "Rewards distributed to early supporters within days of TGE. Mining rewards claimable.",
    status: "future" as const,
    strata: "INNER_CORE",
    sig: "0xA9F764",
  },
  {
    phase: "P14",
    dateShort: "EARLY 2027",
    date: "Early 2027",
    title: "Utility Launch",
    detail: "Platform utility features go live. Enhanced ecosystem functionality.",
    status: "future" as const,
    strata: "OUTER_LAYER",
    sig: "0xA9F7D7",
  },
] as const

export type RoadmapEra = { id: string; label: string; phases: readonly string[] }

export const ROADMAP_ERAS: readonly RoadmapEra[] = [
  { id: "foundation", label: "FOUNDATION", phases: ["P01", "P02"] },
  { id: "economy", label: "LOOT ECONOMY", phases: ["P03", "P04", "P07", "P09"] },
  { id: "community", label: "COMMUNITY & GOVERNANCE", phases: ["P05", "P06", "P08", "P10", "P11"] },
  { id: "launch", label: "TOKEN LAUNCH", phases: ["P12", "P13", "P14"] },
]

/** Milestones in era-flattened (visual reading) order — the single source of
 *  truth for active index, refs, and keyboard nav in the roadmap view. */
export function flattenErasToMilestones(): readonly RoadmapMilestone[] {
  const byPhase = new Map(ROADMAP_MILESTONES.map((m) => [m.phase, m]))
  return ROADMAP_ERAS.flatMap((era) =>
    era.phases.map((p) => {
      const m = byPhase.get(p)
      if (!m) throw new Error(`ROADMAP_ERAS: unknown phase "${p}" in era "${era.id}"`)
      return m
    }),
  )
}

/** Dev-time invariant: every milestone appears in exactly one era. Throws loudly
 *  so an added-but-unslotted milestone can't silently vanish from the roadmap. */
export function assertRoadmapErasValid(): void {
  const flat = flattenErasToMilestones()
  if (flat.length !== ROADMAP_MILESTONES.length) {
    const slotted = new Set(flat.map((m) => m.phase))
    const missing = ROADMAP_MILESTONES.filter((m) => !slotted.has(m.phase)).map((m) => m.phase)
    throw new Error(`ROADMAP_ERAS: ${missing.length} milestone(s) not assigned to an era: ${missing.join(", ")}`)
  }
  const seen = new Set<string>()
  for (const m of flat) {
    if (seen.has(m.phase)) throw new Error(`ROADMAP_ERAS: phase "${m.phase}" assigned to more than one era`)
    seen.add(m.phase)
  }
}

export const CORE_STATS = [
  { label: "Mining Allocation", value: "30%", note: "Pre-TGE fair distribution" },
  { label: "Founders at TGE", value: "0%", note: "Team tokens unlocked at launch" },
  { label: "Max Staking APY", value: "60%", note: "Absolute Zero 365-day pool" },
] as const

/**
 * Canonical token / doc facts. Previously these lived only as hand-typed display
 * copy scattered across view components (tokenomics-view, whitepaper-view, footer),
 * which meant machine-readable surfaces (/stats.json, /llms-full.txt) had nothing
 * to import. Centralized here so there is one source of truth to update.
 */
export const TOKEN_NAME = "RIVYU" as const
export const TOKEN_SYMBOL = "RIV" as const
export const TOTAL_SUPPLY = 1_000_000_000 as const
export const TOTAL_SUPPLY_LABEL = "1,000,000,000" as const
export const WHITEPAPER_VERSION = "V1.4" as const

// Self-check: allocation splits must total 100%. Fails the build loudly if a
// tranche value is fat-fingered, rather than silently shipping a wrong split.
{
  const pctTotal = TOKENOMICS_DISTRIBUTION.reduce((sum, d) => sum + d.value, 0)
  if (pctTotal !== 100) {
    throw new Error(`dapp-content: TOKENOMICS_DISTRIBUTION values sum to ${pctTotal}%, expected 100%`)
  }
}
